import {
  type UIEventHandler,
  useCallback,
  useRef,
  useState
} from 'react'

// Threshold in pixels for considering "at bottom" - allows for minor rounding differences
const BOTTOM_THRESHOLD = 50

/**
 * Hook for managing chat scroll behavior
 * Handles auto-scroll during streaming while respecting user scroll intent
 */
export const useScroll = () => {
  const messagesStartRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isAutoScrolling = useRef(false)

  const [isAtTop, setIsAtTop] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [userScrolled, setUserScrolled] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)

  const handleScroll: UIEventHandler<HTMLDivElement> = useCallback(e => {
    const target = e.target as HTMLDivElement

    // Use threshold for bottom detection - more forgiving than exact equality
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight
    const bottom = distanceFromBottom <= BOTTOM_THRESHOLD
    setIsAtBottom(bottom)

    const top = target.scrollTop === 0
    setIsAtTop(top)

    // Track if user intentionally scrolled away from bottom
    if (!bottom && !isAutoScrolling.current) {
      setUserScrolled(true)
    } else if (bottom) {
      setUserScrolled(false)
    }

    const isOverflow = target.scrollHeight > target.clientHeight
    setIsOverflowing(isOverflow)
  }, [])

  const scrollToTop = useCallback(() => {
    if (messagesStartRef.current) {
      messagesStartRef.current.scrollIntoView({ behavior: 'instant' })
    }
  }, [])

  // Instant scroll - for initial load or user-triggered scroll
  const scrollToBottom = useCallback(() => {
    isAutoScrolling.current = true
    setUserScrolled(false)

    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'instant' })
      }
      isAutoScrolling.current = false
    }, 10)
  }, [])

  // Smooth scroll - for streaming content updates (less jarring)
  const scrollToBottomSmooth = useCallback(() => {
    if (userScrolled) return // Don't auto-scroll if user scrolled up

    isAutoScrolling.current = true

    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }

    setTimeout(() => {
      isAutoScrolling.current = false
    }, 100)
  }, [userScrolled])

  return {
    messagesStartRef,
    messagesEndRef,
    scrollContainerRef,
    isAtTop,
    isAtBottom,
    userScrolled,
    isOverflowing,
    handleScroll,
    scrollToTop,
    scrollToBottom,
    scrollToBottomSmooth,
    setIsAtBottom,
    setUserScrolled
  }
}
